import { useMemo, useState } from "react";
import {
  Autocomplete,
  CircularProgress,
  TextField,
  type TextFieldProps,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type { GetSitesByIdsResponse, SiteSummary } from "@aeriosiq/types";

/**
 * Theme-A polish (2026-05-08, second iteration) — searchable async
 * multi-select filter for sites (parks). Mirrors `ProjectFilter` but
 * sources from `/sites?q=…`.
 */
export interface SiteFilterProps {
  value: string[];
  onChange: (next: string[]) => void;
}

export function SiteFilter({ value, onChange }: SiteFilterProps) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Always send `q` (even empty) — the real BE requires exactly one of
  // `q`/`ids` and treats `q=""` as "match all, top-50 recent" per
  // contracts/lyse/v1/openapi.yaml §/sites.
  const searchQuery = useQuery<GetSitesByIdsResponse>({
    queryKey: ["sites", "search", search],
    enabled: open,
    queryFn: async () => {
      const response = await api.get<GetSitesByIdsResponse>("/sites", {
        params: { q: search },
      });
      return response.data;
    },
    staleTime: 30_000,
  });

  const idsKey = value.slice().sort().join(",");
  const idsQuery = useQuery<GetSitesByIdsResponse>({
    queryKey: ["sites", "by-ids", idsKey],
    enabled: value.length > 0,
    queryFn: async () => {
      const response = await api.get<GetSitesByIdsResponse>("/sites", {
        params: { ids: value.join(",") },
      });
      return response.data;
    },
    staleTime: 5 * 60_000,
  });

  const options = useMemo<SiteSummary[]>(() => {
    const map = new Map<string, SiteSummary>();
    for (const s of idsQuery.data?.items ?? []) map.set(s.id, s);
    for (const s of searchQuery.data?.items ?? []) map.set(s.id, s);
    return [...map.values()];
  }, [idsQuery.data?.items, searchQuery.data?.items]);

  const selectedObjects = useMemo<SiteSummary[]>(() => {
    return value.map(
      (id): SiteSummary =>
        idsQuery.data?.items.find((s) => s.id === id) ?? {
          id,
          name: id,
          code: "",
        },
    );
  }, [value, idsQuery.data?.items]);

  return (
    <Autocomplete<SiteSummary, true>
      multiple
      disableCloseOnSelect
      size="small"
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
        // Clear the typed search string on close so reopening shows
        // the full top-50 again instead of the previous narrowed slice.
        // Mirrors the Status filter ergonomic (60_TEMPLATE_DATA_PAGE.md).
        setSearch("");
      }}
      options={options}
      value={selectedObjects}
      filterOptions={(x) => x}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={searchQuery.isPending && open}
      onChange={(_e, next) => {
        onChange(next.map((s) => s.id));
        // Match Status filter: after picking, reset the typed query so
        // the dropdown immediately re-shows the full option list (the
        // popup stays open via `disableCloseOnSelect`).
        setSearch("");
      }}
      onInputChange={(_e, v, reason) => {
        if (reason === "input") setSearch(v);
      }}
      // Fixed width — Christian, 2026-05-13. Sized so a single typical
      // park-name chip plus the MUI input-cursor min-width (~80 px)
      // tile on ONE line; the field grows in height only when 2+
      // chips force a wrap.
      sx={{ width: 280 }}
      renderInput={(p) => {
        const { InputLabelProps, InputProps, inputProps, ...rest } = p;
        const tfProps = {
          ...rest,
          label: "Park",
          slotProps: {
            htmlInput: inputProps,
            input: {
              ...InputProps,
              endAdornment: (
                <>
                  {searchQuery.isPending && open ? (
                    <CircularProgress size={14} />
                  ) : null}
                  {InputProps.endAdornment}
                </>
              ),
            },
            inputLabel: { ...InputLabelProps, shrink: true },
          },
        } as unknown as TextFieldProps;
        return <TextField {...tfProps} />;
      }}
    />
  );
}
